/**
 * Load optional theme features when their markup exists, including content inserted
 * by predictive search, Quick Add and the section renderer. Cart and
 * purchase modules keep their early execution paths in scripts.liquid.
 */
const features = new Map([
  ['accordion-custom', () => import('@theme/accordion-custom')],
  ['deferred-media', () => import('@theme/media')],
  ['product-model', () => import('@theme/media')],
  ['media-gallery', () => import('@theme/media-gallery')],
  ['product-sku-component', () => import('@theme/product-sku')],
  ['product-inventory', () => import('@theme/product-inventory')],
  ['show-more-component', () => import('@theme/show-more')],
  ['layered-slideshow-component', () => import('@theme/layered-slideshow')],
  ['anchored-popover-component', () => import('@theme/anchored-popover')],
  ['floating-panel-component', () => import('@theme/floating-panel')],
  ['video-background-component', () => import('@theme/video-background')],
  ['rte-formatter', () => import('@theme/rte-formatter')],
  ['volume-pricing', () => import('@theme/volume-pricing')],
  ['price-per-item', () => import('@theme/price-per-item')],
  ['volume-pricing-info', () => import('@theme/volume-pricing-info')],
]);
const selector = [...features.keys()].join(',');
const loading = new Map();
const pendingButtons = new WeakSet();

/** @param {Element} element */
function loadFeature(element) {
  const name = element.localName;
  if (loading.has(name)) return loading.get(name);
  const load = features.get(name);
  if (!load) return;
  features.delete(name);
  const promise = load().then(() => true).catch((error) => {
    features.set(name, load);
    loading.delete(name);
    console.warn(`[theme] Could not load <${name}>:`, error);
    return false;
  });
  loading.set(name, promise);
  return promise;
}

// Keep the first click while an optional module is downloading. Window capture
// runs before Component's document delegate; native forms and links stay native.
window.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[on\\:click]') : null;
  if (!(button instanceof HTMLButtonElement) || button.type !== 'button') return;
  const host = button.closest(selector);
  if (!host || customElements.get(host.localName)) return;
  const promise = loadFeature(host);
  if (!promise) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (pendingButtons.has(button)) return;
  pendingButtons.add(button);
  promise.then((loaded) => {
    pendingButtons.delete(button);
    if (loaded && customElements.get(host.localName) && button.isConnected) button.click();
  });
}, { capture: true });

/** @param {Document | Element} root */
function scan(root) {
  if (root instanceof Element) loadFeature(root);
  for (const element of root.querySelectorAll(selector)) loadFeature(element);
}

function initialize() {
  scan(document);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) scan(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
